import { ContractTerm } from '@/pages/ServiceWizard/ServiceWizard.types'
import { contractTerms } from '@/pages/ServiceWizard/mocks/ServiceWizard.mocks'
import { cn } from '@/lib/misc/Utils'
import { m } from 'framer-motion'
import { useMemo, useEffect, useRef, useState, useLayoutEffect } from 'react'

interface WizardContractTermProps {
  inline?: boolean
  contract?: number
  setContractTerm: (term: ContractTerm) => void
  minContractTerm?: number | undefined
  description?: string
  title?: string
  showTitle?: boolean
  setDefaultValue?: boolean
  showLessThanMinimumContractTerm?: boolean
  styles?: {
    container?: string
    active?: string
    inactive?: string
    disabled?: string
    hover?: string
    item?: string
    containerInside?: string
  }
}

const WizardContractTerm = ({
  inline = true,
  contract,
  setContractTerm,
  minContractTerm,
  description,
  title,
  showTitle = true,
  setDefaultValue = true,
  showLessThanMinimumContractTerm = false,
  styles,
}: WizardContractTermProps) => {
  const filteredContractTerms = useMemo(
    () =>
      contractTerms.filter(
        (term) =>
          !minContractTerm ||
          showLessThanMinimumContractTerm ||
          term.period >= minContractTerm
      ),
    [showLessThanMinimumContractTerm, minContractTerm]
  )

  const contractPeriod = useMemo(
    () =>
      setDefaultValue ? contract || filteredContractTerms[0].period : contract,
    [contract, filteredContractTerms, setDefaultValue]
  )

  const termRefs = useRef<(HTMLButtonElement | null)[]>([])
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 })

  const containerVariants = {
    hidden: {},
    visible: { transition: { staggerChildren: 0.1, delayChildren: 0.1 } },
  }

  useLayoutEffect(() => {
    if (!contractPeriod || filteredContractTerms.length === 0) return

    const updatePosition = () => {
      const selectedIndex = filteredContractTerms.findIndex(
        (term) => term.period === contractPeriod
      )
      if (selectedIndex !== -1 && termRefs.current[selectedIndex]) {
        const termElement = termRefs.current[selectedIndex]
        if (termElement && termElement.parentElement) {
          const termRect = termElement.getBoundingClientRect()
          const containerRect =
            termElement.parentElement.getBoundingClientRect()

          setIndicatorStyle({
            left: termRect.left - containerRect.left,
            width: termRect.width,
          })
        }
      }
    }

    requestAnimationFrame(updatePosition)
  }, [contractPeriod, filteredContractTerms])

  useEffect(() => {
    if (!contractPeriod || filteredContractTerms.length === 0) return

    const fallbackTimer = setTimeout(() => {
      const selectedIndex = filteredContractTerms.findIndex(
        (term) => term.period === contractPeriod
      )
      if (selectedIndex !== -1 && termRefs.current[selectedIndex]) {
        const termElement = termRefs.current[selectedIndex]
        if (termElement && termElement.parentElement) {
          const termRect = termElement.getBoundingClientRect()
          const containerRect =
            termElement.parentElement.getBoundingClientRect()

          setIndicatorStyle({
            left: termRect.left - containerRect.left,
            width: termRect.width,
          })
        }
      }
    }, 300)

    return () => clearTimeout(fallbackTimer)
  }, [contractPeriod, filteredContractTerms])

  const onContractTermClick = (term: ContractTerm) => {
    if (minContractTerm && minContractTerm > term.period) {
      return
    }
    setContractTerm(term)
  }

  return (
    <div
      data-cy="contract-term"
      className={cn(
        'flex justify-between flex-wrap flex-col max-md:mb-4',
        !inline && 'flex-col',
        styles?.container
      )}
    >
      {showTitle && (
        <h3 className="text-neutral-90 text-2xl leading-[120%] tracking-[-0.24px] mb-3 md:leading-[normal] md:tracking-[-0.48px]">
          {title ?? 'Select contract term'}
        </h3>
      )}

      {description && <p className="text-neutral-50">{description}</p>}

      <m.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className={cn(
          'relative gap-x-3 p-1 border-1 rounded-lg',
          inline
            ? 'flex w-fit border-accent-30 max-md:gap-x-1'
            : `grid grid-cols-${filteredContractTerms.length} w-full bg-surface border-hairline px-1`,
          styles?.containerInside
        )}
      >
        {/* Animated background indicator */}
        <m.div
          className="absolute top-1 bottom-1 bg-accent-60 rounded-lg"
          animate={{
            left: indicatorStyle.left,
            width: indicatorStyle.width,
          }}
          transition={{
            type: 'spring',
            stiffness: 400,
            damping: 30,
          }}
          style={{
            zIndex: 0,
          }}
        />
        {filteredContractTerms.map((term, index) => (
          <button
            key={term.label_short}
            type="button"
            ref={(el) => {
              termRefs.current[index] = el
            }}
            data-cy={`wizard-contract-term-${term.label_short}`}
            aria-pressed={contractPeriod === term.period}
            onClick={() => onContractTermClick(term)}
            className={cn(
              'relative z-10 rounded-lg py-1 px-2 text-center font-normal tracking-tightest transition-opacity duration-300',
              inline
                ? 'text-sm max-md:px-3.5 max-md:py-2.5 max-md:text-sm'
                : 'py-3 text-sm',
              contractPeriod === term.period
                ? 'text-white ' + styles?.active
                : 'text-accent-60 bg-transparent! ' + styles?.inactive,
              minContractTerm && term.period < minContractTerm
                ? 'opacity-50 cursor-not-allowed ' + styles?.disabled
                : 'cursor-pointer hover:opacity-75 ' + styles?.hover,
              styles?.item
            )}
          >
            {inline ? term.label_short : term.label}
          </button>
        ))}
      </m.div>
    </div>
  )
}
export default WizardContractTerm
